import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {Button, ButtonToolbar} from 'react-bootstrap';
import classNames from 'classnames';
import filter from 'lodash/filter';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import size from 'lodash/size';
import sortBy from 'lodash/sortBy';
import Mousetrap from 'mousetrap';
import ReactTooltip from 'react-tooltip'
import {ENHANCEMENT, ORIGINAL, POPPED} from '../Constants';

export class PresetsToolbar extends Component {
    static propTypes = {
        imageId: PropTypes.number.isRequired,
        amplitude: PropTypes.number.isRequired,
        spread: PropTypes.number.isRequired,
        presets: PropTypes.object.isRequired,
        activeImage: PropTypes.string,
        onActiveImageChanged: PropTypes.func.isRequired,
        onAmplitudeOrScaleChange: PropTypes.func.isRequired,
        onImageViewerToggleOriginal: PropTypes.func.isRequired,
        onImageViewerSetPreferred: PropTypes.func.isRequired,
        isOriginalActive: PropTypes.bool.isRequired,
        preferredAmplitude: PropTypes.number.isRequired,
        preferredSpread: PropTypes.number.isRequired,
    };

    componentDidMount() {
        for (let i = 1; i <= 9; ++i) {
            Mousetrap.bind(`${i}`, (e) => {
                e.preventDefault();
                this.handleSelectPresetNumber(i);
            });
        }
        Mousetrap.bind('0', (e) => {
            e.preventDefault();
            this.handleSelectCustom();
        });
        Mousetrap.bind('c', (e) => {
            e.preventDefault();
            this.toggleContrast();
        });
        Mousetrap.bind('P', (e) => {
            e.preventDefault();
            this.props.onImageViewerSetPreferred();
        });
    }

    componentWillUnmount() {
        for (let i = 1; i <= 9; ++i) {
            // noinspection JSUnresolvedFunction
            Mousetrap.unbind(`${i}`);
        }
        Mousetrap.unbind(['0', 'c', 'P']);
    }

    findMatchingPreset = (presets, amplitude, spread) => {
        const matchingPresets = filter(presets, {amplitude, spread});
        return isEmpty(matchingPresets) ? null : matchingPresets[0];
    };

    activateOriginal = () => {
        const {onActiveImageChanged} = this.props;
        onActiveImageChanged(ORIGINAL);
    };

    toggleContrast = () => {
        const {onActiveImageChanged, activeImage} = this.props;
        onActiveImageChanged(activeImage === ENHANCEMENT ? POPPED : ENHANCEMENT);
    };

    activateContrast = () => {
        // const {onActiveImageChanged, activeImage} = this.props;
        // onActiveImageChanged(activeImage === ENHANCEMENT ? POPPED : ENHANCEMENT);
        const {onActiveImageChanged} = this.props;
        onActiveImageChanged(ENHANCEMENT);
    };

    activatePopped = () => {
        const {onActiveImageChanged} = this.props;
        onActiveImageChanged(POPPED);
    };

    handleSelectPreset = (preset) => {
        const {onAmplitudeOrScaleChange, imageId} = this.props;
        onAmplitudeOrScaleChange(imageId, preset, true);
    };

    handleSelectPresetNumber = (presetNumber) => {
        const {presets} = this.props;
        const preset = find(presets, {presetNumber});
        if (preset) {
            this.handleSelectPreset(preset);
        }
    };

    handleSelectCustom = () => {
        const {amplitude, spread} = this.props;
        this.handleSelectPreset({amplitude, spread});
    };

    render() {
        const {
            activeImage,
            isOriginalActive, amplitude, spread, preferredAmplitude, preferredSpread
        } = this.props;
        const presets = filter(this.props.presets, p => p.presetNumber > 0);
        const matchingPreset = this.findMatchingPreset(presets, amplitude, spread);
        const presetMatchingPreferred = this.findMatchingPreset(presets, preferredAmplitude, preferredSpread);
        const currentIsPreferred = amplitude === preferredAmplitude && spread === preferredSpread;
        const count = size(presets);
        if (count === 0 || (count === 1 && presetMatchingPreferred)) {
            return false;
        }
        // const preferredControl = <div className="preferred-preset">{'★'}</div>;
        const preferredControl = <div className="preferred-preset"/>;

        return (
            <div className="presets-toolbar">
                <ButtonToolbar>
                    {sortBy(filter(presets, p => p.presetNumber >= 1 && p.presetNumber <= 9), 'presetNumber').map((preset) => {
                        const presetNumber = preset.presetNumber;
                        const current = matchingPreset && preset.id === matchingPreset.id;
                        const active = !isOriginalActive && current;
                        const isPreferred = presetMatchingPreferred && preset.id === presetMatchingPreferred.id;
                        return (
                            <Button
                                key={presetNumber}
                                bsStyle="link"
                                data-tip data-for={`preset-icon-tip-${presetNumber}`}
                                className={classNames('preset-icon', {'preferred-preset': isPreferred, current})}
                                active={active}
                                onClick={() => this.handleSelectPreset(preset)}
                            >
                                {presetNumber}
                                {isPreferred && preferredControl}
                                <ReactTooltip id={`preset-icon-tip-${presetNumber}`} place="top" delayShow={400} className="light-tool-tip" type="light">
                                    <div>{`Preset "${preset.name}" - Amp: ${preset.amplitude}, Spread: ${preset.spread}`}</div>
                                    <kbd>{`${presetNumber}`}</kbd>
                                </ReactTooltip>
                            </Button>
                        );
                    })}
                    {!matchingPreset &&
                    <Button
                        key="no-preset"
                        bsStyle="link"
                        data-tip data-for="no-preset-icon-tip"
                        className={classNames('preset-icon current', {'preferred-preset': currentIsPreferred})}
                        active={!isOriginalActive}
                        onClick={this.handleSelectCustom}
                    >
                        {'ϑ'}
                        {preferredControl}
                        <ReactTooltip id="no-preset-icon-tip" place="top" delayShow={400} className="light-tool-tip" type="light">
                            <div>{`Manual - Amp: ${amplitude}, Spread: ${spread}`}</div>
                            <kbd>{'0'}</kbd>
                        </ReactTooltip>
                    </Button>
                    }
                    <Button
                        key="contrast"
                        bsStyle="link"
                        data-tip data-for={'preset-contrast-icon-tip'}
                        className={classNames('preset-icon preset-contrast', {current: activeImage === ENHANCEMENT})}
                        onClick={this.toggleContrast}
                        active={!isOriginalActive && activeImage === ENHANCEMENT}
                    >
                        {'C'}
                        <ReactTooltip id="preset-contrast-icon-tip" place="top" delayShow={400} className="light-tool-tip" type="light">
                            <div>{'Toggle contrast image'}</div>
                            <kbd>{'c'}</kbd>
                        </ReactTooltip>
                    </Button>
                </ButtonToolbar>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/PresetsToolbar.js